import clsx from 'clsx'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { match } from 'ts-pattern'

import Header from '@/components-latest/layout/Header'
import Button from '@/components-latest/primitives/Button'
import { Plus } from '@/components-latest/primitives/Icons'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import Tabs, { Tab } from '@/components-latest/shared/Tabs'
import BuyXGetYItem from '@/components-latest/ui/OfferPage/BuyXGetYItem/BuyXGetYItem'
import CartDiscountItem from '@/components-latest/ui/OfferPage/CartDiscountItem/CartDiscountItem'
import CreateOfferModal from '@/components-latest/ui/OfferPage/CreateOfferModal/CreateOfferModal'
import OfferItem from '@/components-latest/ui/OfferPage/OfferItem'
import { SupportedLanguages } from '@/config/i18n'
import { PATHS } from '@/config/router/routes'
import { ApiFetchMenu, MenuMainCategory } from '@/ducks/menu/menu.types'
import {
  useFetchCartDiscountsQuery,
  useFetchOffersBuyXGetYQuery,
  useFetchOffersQuery
} from '@/ducks/offers/api/offers.api'
import { OfferBuyXGetY } from '@/ducks/offers/offers.types'
import { selectStoreType } from '@/ducks/store/selectors/store.selectors'
import { useServerTranslation } from '@/hooks/useServerTranslation/useServerTranslation'
import { useToggle } from '@/hooks/useToggle/useToggle'

import Styles from './OffersPage.module.scss'

const getMenuArray = (
  data: ApiFetchMenu,
  language: SupportedLanguages
): MenuMainCategory[] => {
  return data.menu.mainCategories
    .map(category => {
      const filterItems = category.items
      const filteredSubCategories = category.subCategories
        .map(subCategory => ({
          ...subCategory,
          items: subCategory.items
        }))
        .filter(subCategory => subCategory.items.length > 0)
      return {
        ...category,
        items: filterItems,
        subCategories: filteredSubCategories
      }
    })
    .filter(
      category => category.items.length > 0 || category.subCategories.length > 0
    )
}

export const ORDERS_HEADER_ID = 'orders-header'

type RouteParams = {
  id?: 'item_discount' | 'cart_discount' | 'buyX_getY'
}
interface CartDiscount {
  id: string
  discountType: string
  percentage: number
  status: string
  minimumAmount: number
  maximumOff: number
  expirationTime: string
}
interface LocationState {
  isCreationFormVisibletest?: boolean
}
const OffersPage = () => {
  const { t } = useTranslation()
  const { id: routeId } = useParams<RouteParams>()
  const id = routeId || 'item_discount'
  const { language, getTranslation } = useServerTranslation()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { lmd_store_webapp_offers_page } = useFlags()
  const storeType = useSelector(selectStoreType)
  const history = useHistory()

  const [showCartDiscountError, setShowCartDiscountError] = useState(false)
  const [isCreationFormVisible, toggleCreationFormVisibility] = useToggle(false)
  const [activeEditOfferItem, setActiveEditOfferItem] = useState<{
    name: string
    id: string
    amount: number
    offerValidUntil: string
  } | null>(null)

  const fetchOffersQuery = useFetchOffersQuery(undefined, {
    selectFromResult: ({ data, ...rest }) => {
      const results = data?.menu ? getMenuArray(data, language) : []
      return {
        ...rest,
        data,
        offers: results
      }
    }
  })

  const location = useLocation()
  const state = (location.state as LocationState) || {}
  const isCreationFormVisibletest =
    state?.isCreationFormVisibletest === true ||
    location.pathname === PATHS.OFFERS_CREATE

  const { isSuccess, offers, refetch: refetchOfferQuery } = fetchOffersQuery
  const {
    data: cartDiscountsData,
    isLoading: isCartDiscountsLoading,
    isSuccess: isCartDiscountsSuccess,
    refetch: refetchCartDiscount
  } = useFetchCartDiscountsQuery()
  const {
    data: offerBuyXGetYData,
    isLoading: isofferBuyXGetLoading,
    isSuccess: isofferBuyXGetSuccess,
    refetch: refetchOffersBuyXGetY
  } = useFetchOffersBuyXGetYQuery()
  const mappedCartDiscounts =
    cartDiscountsData?.map(discount => ({
      id: discount._id,
      discountType: discount.discount_type,
      percentage: discount.amount_percentage,
      status: discount.status === 1 ? 'Active' : 'Inactive',
      minimumAmount: discount.target_amount,
      maximumOff: discount.max_off,
      expirationDate: new Date(discount.expiry_date).toLocaleDateString(),
      expirationTime: discount.expiration_time
    })) || []

  const mappedOffersBuyXGetY =
    offerBuyXGetYData?.map(offer => ({
      id: offer._id,
      expiryDate: offer.expiry_date,
      expirationTime: offer.expiration_time,
      offerType: offer.offer_type,
      itemsPurchased: offer.nbr_items_purchased,
      itemsOffer: offer.nbr_items_offer,
      isCumulative: offer.cumulatif,
      item1: {
        id: offer.item1._id,
        name: offer.item1.name
      },
      item2: offer.item2.map(item => ({
        id: item._id,
        name: item.name,
        discountPercent: item.discount_percent
      })),
      status: offer.status
    })) || []

  const handleCreateOfferSubmit = (
    offerType: 'item_discount' | 'cart_discount' | 'buyX_getY'
  ) => {
    if (offerType === 'item_discount') {
      history.push(PATHS.OFFERS_CREATE)
    } else if (offerType === 'cart_discount') {
      if (cartDiscountsData && cartDiscountsData.length > 0) {
        setShowCartDiscountError(true)
      } else {
        history.push(PATHS.OFFERS_CART_DISCOUNT_CREATE)
      }
    } else if (offerType === 'buyX_getY') {
      history.push(PATHS.OFFERS_BUYX_GETY_CREATE)
    }
    setIsModalVisible(false)
  }
  useEffect(() => {
    const refetchMap = {
      cart_discount: refetchCartDiscount,
      item_discount: refetchOfferQuery,
      buyX_getY: refetchOffersBuyXGetY
    }

    refetchMap[id]?.()
  }, [id, refetchCartDiscount, refetchOfferQuery, refetchOffersBuyXGetY])

  const handleEditCartOfferSubmit = (discount: CartDiscount) => {
    history.push(PATHS.OFFERS_CART_DISCOUNT_CREATE, {
      state: {
        offerId: discount.id,
        initialDiscount: discount.percentage,
        initialExpiration: discount.expirationTime,
        initialMinimumAmount: discount.minimumAmount,
        initialMaximumOff: discount.maximumOff,
        initialDiscountType:
          discount.discountType && discount.discountType === 'Percentage'
            ? 'Percentage'
            : 'Flat'
      }
    })
    setIsModalVisible(false)
  }
  const handleEditOfferBuyXGetYSubmit = (offer: OfferBuyXGetY) => {
    history.push(PATHS.OFFERS_BUYX_GETY_CREATE, {
      state: {
        offerId: offer.id,
        expiryDate: offer.expiryDate,
        expirationTime: offer.expirationTime,
        itemsPurchased: offer.itemsPurchased,
        itemsOffer: offer.itemsOffer,
        isCumulative: offer.isCumulative,
        item1: {
          id: offer.item1.id,
          name: offer.item1.name
        },
        item2:
          offer.item2.length > 0
            ? {
                id: offer.item2[0].id,
                name: offer.item2[0].name,
                discountPercent: offer.item2[0].discountPercent
              }
            : null,
        status: offer.status
      }
    })

    setIsModalVisible(false)
  }

  const handleGoToActiveDiscounts = () => {
    setShowCartDiscountError(false)
    history.push('/offers/cart_discount')
  }

  if (!lmd_store_webapp_offers_page || storeType === 'dark_store') return null

  const footer = (
    <div className={Styles.footer}>
      <Button
        onClick={() => setShowCartDiscountError(false)}
        variant="secondary">
        {t('close')}
      </Button>
      <Button variant="primary" onClick={handleGoToActiveDiscounts}>
        {t('offers.cart-discount-error.button')}
      </Button>
    </div>
  )

  return (
    <div className={Styles.offersPage}>
      {!isCreationFormVisibletest && (
        <>
          <Header title={t('offers.offers-title')}>
            {isSuccess &&
              ((offers?.length ?? 0) > 0 ||
                (cartDiscountsData?.length ?? 0) > 0 ||
                (offerBuyXGetYData?.length ?? 0) > 0) && (
                <Button
                  onClick={() => setIsModalVisible(true)}
                  shape="rounded"
                  variant="branded"
                  icon={<Plus />}>
                  {t('offers.create-offer')}
                </Button>
              )}
          </Header>
          <Tabs asNavigation className={Styles.tabs}>
            <Tab id="/offers/item_discount">{t('offers.item-discount-tab.title')}</Tab>
            <Tab id="/offers/cart_discount">{t('offers.cart-discount-tab.title')}</Tab>
            <Tab id="/offers/buyX_getY">{t('offers.buyX-getY-tab.title')}</Tab>
          </Tabs>
        </>
      )}
      {/* Create Offer Modal */}
      {isModalVisible && (
        <CreateOfferModal
          isOpen={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onSubmit={handleCreateOfferSubmit}
          toggleCreationFormVisibility={toggleCreationFormVisibility}
        />
      )}

      {/* Cart Discount Error Modal */}
      {showCartDiscountError && (
        <CommonModal
          open={showCartDiscountError}
          title={t('offers.cart-discount-error.title')}
          onOpenChange={() => setShowCartDiscountError(false)}
          body={
            <Text variant="body">
              {t('offers.cart-discount-error.description')}
            </Text>
          }
          footer={footer}
        />
      )}

      {/* Render the item discount list only when the selected tab is "item_discount" */}
      {!isCreationFormVisible &&
        id === 'item_discount' &&
        match(fetchOffersQuery)
          .with({ isFetching: true }, () => (
            <div className={Styles.spinner}>
              <Spinner />
            </div>
          ))
          .when(
            ({ offers, isSuccess }) => isSuccess && offers.length === 0,
            () => (
              <div className={Styles.empty}>
                <img
                  src="/images/offers/empty.svg"
                  alt="coin with percent symbol"
                />
                <Text variant="heading-04">{t('offers.empty.no-offers')}</Text>
                <Text variant="subtitle" align="center">
                  {t('offers.empty.no-offers-description')}
                </Text>
                <Button
                  onClick={() => setIsModalVisible(true)}
                  shape="rounded"
                  variant="branded"
                  icon={<Plus />}>
                  {t('offers.create-offer')}
                </Button>
              </div>
            )
          )
          .when(
            ({ offers, isSuccess }) => isSuccess && offers.length > 0,
            ({ offers, isFetching, isLoading }) => {
              return (
                <div
                  className={clsx(Styles.content, Styles.offersList, {
                    [Styles.faded]: isLoading
                  })}>
                  {offers.map(category => (
                    <div
                      className={clsx(Styles.categoryBlock, {
                        [Styles.faded]: isFetching
                      })}
                      key={category._id}>
                      {category.items && category.items.length > 0 && (
                        <div className={Styles.categoryBlock}>
                          <Text
                            className={Styles.categoryTitle}
                            variant="nextBody"
                            weight="semiBold">
                            {category.name[language]}
                          </Text>
                          {category.items.map(item => (
                            <OfferItem
                              key={item._id}
                              menuItem={item}
                              onEdit={() => {
                                setActiveEditOfferItem({
                                  name: getTranslation(item.name),
                                  id: item._id,
                                  amount: item.offer?.amount || 0,
                                  offerValidUntil:
                                    item.offer?.offer_validity || ''
                                })
                              }}
                            />
                          ))}
                        </div>
                      )}

                      {category?.subCategories?.length > 0 &&
                        category.subCategories.map(subCategory =>
                          subCategory.items?.length > 0 ? (
                            <div
                              className={Styles.categoryBlock}
                              key={subCategory._id}>
                              <Text
                                className={Styles.categoryTitle}
                                variant="nextBody"
                                weight="semiBold">
                                {`${category.name[language]} / ${subCategory.name[language]}`}
                              </Text>
                              {subCategory.items.map(item => (
                                <OfferItem
                                  onEdit={() => {
                                    setActiveEditOfferItem({
                                      name: getTranslation(item.name),
                                      id: item._id,
                                      amount: item.offer?.amount || 0,
                                      offerValidUntil:
                                        item.offer?.offer_validity || ''
                                    })
                                  }}
                                  onDisable={() => {}}
                                  key={item._id}
                                  menuItem={item}
                                />
                              ))}
                            </div>
                          ) : null
                        )}
                    </div>
                  ))}
                </div>
              )
            }
          )
          .otherwise(() => null)}
      {/* Render the cart discount list */}
      {!isCreationFormVisible &&
        id === 'cart_discount' &&
        match({
          isCartDiscountsLoading,
          isCartDiscountsSuccess,
          mappedCartDiscounts
        })
          .with({ isCartDiscountsLoading: true }, () => (
            <div className={Styles.spinner}>
              <Spinner />
            </div>
          ))
          .when(
            ({ isCartDiscountsSuccess, mappedCartDiscounts }) =>
              isCartDiscountsSuccess && mappedCartDiscounts.length === 0,
            () => (
              <div className={Styles.empty}>
                <img
                  src="/images/offers/empty.svg"
                  alt="coin with percent symbol"
                />
                <Text variant="heading-04">{t('offers.empty.no-offers')}</Text>
                <Text variant="subtitle" align="center">
                  {t('offers.empty.no-offers-description')}
                </Text>
                <Button
                  onClick={() => setIsModalVisible(true)}
                  shape="rounded"
                  variant="branded"
                  icon={<Plus />}>
                  {t('offers.create-offer')}
                </Button>
              </div>
            )
          )
          .when(
            ({ isCartDiscountsSuccess, mappedCartDiscounts }) =>
              isCartDiscountsSuccess && mappedCartDiscounts.length > 0,
            ({ mappedCartDiscounts }) => (
              <div className={Styles.offersDiscount}>
                {mappedCartDiscounts.map((discount, index) => (
                  <CartDiscountItem
                    key={index}
                    discount={discount}
                    onEdit={() => handleEditCartOfferSubmit(discount)}
                    onDelete={() => {}}
                  />
                ))}
              </div>
            )
          )
          .otherwise(() => null)}
      {!isCreationFormVisible &&
        id === 'buyX_getY' &&
        match({
          isofferBuyXGetLoading,
          isofferBuyXGetSuccess,
          mappedOffersBuyXGetY
        })
          .with({ isofferBuyXGetLoading: true }, () => (
            <div className={Styles.spinner}>
              <Spinner />
            </div>
          ))
          .when(
            ({ isofferBuyXGetSuccess, mappedOffersBuyXGetY }) =>
              isofferBuyXGetSuccess && mappedOffersBuyXGetY.length === 0,
            () => (
              <div className={Styles.empty}>
                <img
                  src="/images/offers/empty.svg"
                  alt="coin with percent symbol"
                />
                <Text variant="heading-04">{t('offers.empty.no-offers')}</Text>
                <Text variant="subtitle" align="center">
                  {t('offers.empty.no-offers-description')}
                </Text>
                <Button
                  onClick={() => setIsModalVisible(true)}
                  shape="rounded"
                  variant="branded"
                  icon={<Plus />}>
                  {t('offers.create-offer')}
                </Button>
              </div>
            )
          )
          .when(
            ({ isofferBuyXGetSuccess, mappedOffersBuyXGetY }) =>
              isofferBuyXGetSuccess && mappedOffersBuyXGetY.length > 0,
            ({ mappedOffersBuyXGetY }) => (
              <div className={Styles.offersDiscount}>
                {mappedOffersBuyXGetY.map((offerBuyXGetY, index) => (
                  <BuyXGetYItem
                    key={index}
                    offer={offerBuyXGetY}
                    onEdit={() => handleEditOfferBuyXGetYSubmit(offerBuyXGetY)}
                    onDelete={() => {}}
                  />
                ))}
              </div>
            )
          )
          .otherwise(() => null)}
    </div>
  )
}

export default OffersPage
